import { useCallback, useContext, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { PanelContext } from '@components/Panel/PanelContext'
import { useAppTransition } from 'contexts/AppTransitionContext'
import { useDevice } from 'hooks/useDevice'
import { CHANNEL_INFO_PARAMS_VALUES } from 'routes'
import { Address } from 'use-towns-client'

export const usePanelActions = () => {
    const { isTouch } = useDevice()
    const [searchParams, setSearchParams] = useSearchParams()
    const navigate = useNavigate()
    const context = useContext(PanelContext)
    const contextStackId = context?.stackId
    const contextParentRoute = context?.parentRoute
    const isRootPanel = context?.isRootPanel

    // Ensure searchParams is always available and stable
    const stableSearchParams = useMemo(() => searchParams || new URLSearchParams(), [searchParams])

    const { startTransition } = useAppTransition()

    return {
        data: useMemo(() => stableSearchParams.get('data'), [stableSearchParams]),
        isStacked: useMemo(() => {
            if (isTouch) {
                return !isRootPanel
            } else {
                return stableSearchParams.get('stacked')
            }
        }, [isRootPanel, isTouch, stableSearchParams]),
        openPanel: useCallback(
            (
                panel: CHANNEL_INFO_PARAMS_VALUES,
                options: {
                    stackId?: string
                    channelId?: string
                    channelFormId?: string // unique identifier for create channel form
                    /** The onchain address of the user. If it's an account abstraction, we need to use the root key address. */
                    profileId?: string
                    roleId?: string
                    data?: string
                    roles?: 'new'
                    assetSource?: Address
                    eventId?: string
                    streamId?: string
                    mode?: 'buy' | 'sell'
                    tokenAddress?: string
                    chainId?: string
                    spaceId?: string
                    depositId?: string
                    forceStack?: boolean
                    channelName?: string
                } = {},
            ) => {
                const { stackId = contextStackId, forceStack, ...restOptions } = options
                const shouldStack = contextStackId || forceStack || context?.isPanelContext
                startTransition(() =>
                    setSearchParams({
                        panel,
                        stackId,
                        // for desktop, opening a panel from within a panel should
                        // "stack it" allowing the user to go back to the previous panel
                        ...(shouldStack ? { stacked: 'true' } : {}),
                        ...restOptions,
                    }),
                )
            },
            [context?.isPanelContext, contextStackId, setSearchParams, startTransition],
        ),
        closePanel: useCallback(
            (params?: { preventPopStack?: boolean; force?: boolean }) => {
                if (params?.force) {
                    const newSearchParams = new URLSearchParams(stableSearchParams)
                    newSearchParams.delete('panel')
                    setSearchParams(newSearchParams)
                    return
                }
                if (contextParentRoute) {
                    navigate(contextParentRoute)
                } else if (
                    (stableSearchParams.get('stacked') && !params?.preventPopStack) ||
                    isTouch
                ) {
                    navigate(-1)
                } else if (stableSearchParams.get('panel')) {
                    const newSearchParams = new URLSearchParams(stableSearchParams)
                    newSearchParams.delete('panel')
                    setSearchParams(newSearchParams)
                }
            },
            [contextParentRoute, isTouch, navigate, stableSearchParams, setSearchParams],
        ),

        isPanelOpen: useCallback(
            (panel: CHANNEL_INFO_PARAMS_VALUES) => {
                return stableSearchParams.get('panel') === panel
            },
            [stableSearchParams],
        ),
        currentlyOpenPanel: useMemo(() => stableSearchParams.get('panel'), [stableSearchParams]),
    }
}
